// Mock entities for UI testing

export const User = {
  async list() {
    return [
      {
        id: '1',
        name: 'Admin User',
        full_name: 'Admin User',
        email: 'admin@example.com',
        role: 'admin',
        position: 'Manager',
        status: 'active',
        module_settings: {
          leads: {
            auto_assignment: false,
            assignment_method: 'position',
            assignment_positions: ['Manager'],
            assignment_users: [],
            last_assigned_index: -1
          }
        }
      },
      {
        id: '2',
        name: 'John Doe',
        full_name: 'John Doe',
        email: 'john@example.com',
        role: 'user',
        position: 'Sales',
        status: 'active'
      }
    ];
  },
  async get(id) {
    const users = await this.list();
    return users.find(u => u.id === id);
  },
  async me() {
    const users = await this.list();
    return users[0]; // Return first user as current user
  },
  async update(id, data) {
    return { id, ...data };
  },
  async updateMyUserData(data) {
    const currentUser = await this.me();
    return { ...currentUser, ...data };
  },
  async create(data) {
    return { id: String(Date.now()), ...data };
  },
  async logout() {
    console.log('Mock logout');
    return true;
  }
};

export const Lead = {
  _leads: [
    {
      id: '1',
      first_name: 'Jane',
      last_name: 'Smith',
      company: 'Tech Corp',
      email: 'jane@techcorp.com',
      phone: '+1 555-0100',
      status: 'new',
      source: 'website',
      created_date: '2024-01-15',
      assigned_to: 'admin@example.com'
    },
    {
      id: '2',
      first_name: 'Bob',
      last_name: 'Johnson',
      company: 'Innovation Ltd',
      email: 'bob@innovation.com',
      phone: '+1 555-0200',
      status: 'contacted',
      source: 'referral',
      created_date: '2024-02-20',
      assigned_to: 'john@example.com'
    },
    {
      id: '3',
      first_name: 'Alice',
      last_name: 'Williams',
      company: 'StartUp Inc',
      email: 'alice@startup.com',
      phone: '+1 555-0300',
      status: 'qualified',
      source: 'linkedin',
      created_date: '2024-03-10',
      assigned_to: 'admin@example.com'
    },
    {
      id: '4',
      first_name: 'Carlos',
      last_name: 'Garcia',
      company: 'Enterprise Solutions',
      email: 'carlos@enterprise.com',
      phone: '+1 555-0400',
      status: 'proposal',
      source: 'cold_call',
      created_date: '2024-03-25',
      assigned_to: 'john@example.com'
    }
  ],
  async list() {
    return [...this._leads];
  },
  async get(id) {
    return this._leads.find(l => l.id === id) || null;
  },
  async update(id, data) {
    const idx = this._leads.findIndex(l => l.id === id);
    if (idx !== -1) {
      this._leads[idx] = { ...this._leads[idx], ...data };
      return this._leads[idx];
    }
    return null;
  },
  async create(data) {
    const newLead = {
      id: data.lead_id || String(Date.now()),
      created_date: new Date().toISOString(),
      status: 'new',
      ...data,
    };
    this._leads = [newLead, ...this._leads];
    return newLead;
  }
};

export const Deal = {
  async list() {
    return [
      {
        id: '1',
        title: 'Enterprise Contract',
        amount: 50000,
        status: 'negotiation',
        contact_id: '1',
        contact_name: 'John Doe',
        close_date: '2024-06-30',
        created_date: '2024-01-15'
      },
      {
        id: '2',
        title: 'Small Business Package',
        amount: 15000,
        status: 'proposal',
        contact_id: '2',
        contact_name: 'Jane Smith',
        close_date: '2024-05-15',
        created_date: '2024-02-20'
      }
    ];
  },
  async get(id) {
    const deals = await this.list();
    return deals.find(d => d.id === id);
  },
  async update(id, data) {
    return { id, ...data };
  },
  async create(data) {
    return { id: String(Date.now()), ...data };
  }
};

export const Case = {
  async list() {
    return [
      {
        id: '1',
        case_number: 'CASE-001',
        status: 'in_progress',
        case_type: 'Consultation',
        customer_id: '1',
        customer_name: 'Maria Silva',
        created_date: '2024-01-15'
      },
      {
        id: '2',
        case_number: 'CASE-002',
        status: 'pending',
        case_type: 'Document Review',
        customer_id: '2',
        customer_name: 'João Santos',
        created_date: '2024-02-20'
      }
    ];
  },
  async get(id) {
    const cases = await this.list();
    return cases.find(c => c.id === id);
  },
  async update(id, data) {
    return { id, ...data };
  },
  async create(data) {
    return { id: String(Date.now()), ...data };
  }
};

export const Customer = {
  async list() {
    return [
      {
        id: '1',
        first_name: 'Maria',
        last_name: 'Silva',
        email: 'maria.silva@example.com',
        phone: '+55 11 98765-4321',
        status: 'active',
        country: 'Brazil',
        city: 'São Paulo',
        created_date: '2024-01-15'
      },
      {
        id: '2',
        first_name: 'João',
        last_name: 'Santos',
        email: 'joao.santos@example.com',
        phone: '+55 21 99876-5432',
        status: 'active',
        country: 'Brazil',
        city: 'Rio de Janeiro',
        created_date: '2024-02-20'
      }
    ];
  },
  async get(id) {
    const customers = await this.list();
    return customers.find(c => c.id === id);
  },
  async update(id, data) {
    return { id, ...data };
  },
  async create(data) {
    return { id: String(Date.now()), ...data };
  }
};

export const Contact = {
  async list() {
    return [
      {
        id: '1',
        first_name: 'João',
        last_name: 'Silva',
        full_name: 'João Silva',
        email: 'joao.silva@example.com',
        phone: '+55 11 98765-4321',
        status: 'active',
        country: 'Brazil',
        created_date: '2024-01-15'
      },
      {
        id: '2',
        first_name: 'Maria',
        last_name: 'Santos',
        full_name: 'Maria Santos',
        email: 'maria.santos@example.com',
        phone: '+55 21 99876-5432',
        status: 'active',
        country: 'Brazil',
        created_date: '2024-02-20'
      }
    ];
  },
  async get(id) {
    const contacts = await this.list();
    return contacts.find(c => c.id === id);
  },
  async update(id, data) {
    return { id, ...data };
  },
  async create(data) {
    return { id: String(Date.now()), ...data };
  }
};

export const Service = {
  _services: [
    { id: 's1', name: 'Consulting', price: 1000, status: 'active' },
    { id: 's2', name: 'Implementation', price: 5000, status: 'active' }
  ],
  async list() {
    return [...this._services];
  },
  async get(id) {
    return this._services.find(s => s.id === id) || null;
  },
  async create(data) {
    const newService = { id: data.id || String(Date.now()), status: 'active', ...data };
    this._services = [newService, ...this._services];
    return newService;
  },
  async update(id, data) {
    const idx = this._services.findIndex(s => s.id === id);
    if (idx !== -1) {
      this._services[idx] = { ...this._services[idx], ...data };
      return this._services[idx];
    }
    return null;
  }
};

